import type { TilePosition } from '../../../model/gamestate/GameState';
import type { BiomeType } from '../../../model/gamestate/types';
import { pathAutotiler } from '../../../terrain/pathAutotiler/activePathAutotiler';
import { applyCanonicalPathTileEdit } from '../../../terrain/pathAutotiler/pathTileLayers';
import { encodeFixedPathVariantId } from '../../../terrain/pathAutotiler/pathManualVariant';
import { getPathTileRenderRotationOffset } from '../../../terrain/pathAutotiler/pathTileRenderOrientation';
import {
    recommendPathTileAt,
} from '../../../terrain/pathAutotiler/recommendPathTile';
import {
    getPathTileQuarterAtlasDefinition,
    loadPathTileAtlasImage,
    type PathTileAtlasId,
} from '../../../terrain/pathTileRegistry';
import { getTerrainBiomeDefinition } from '../../../terrain/registry';
import { isRectVisible, type ScreenBounds } from '../culling';
import { drawResolvedMapObjectSprite, resolveMapObjectSpriteMetrics, resolveMapObjectWorldAnchor, type MapObjectSpriteInput } from '../mapObjectSprites';
import {
    drawGridClippedPathTileImage,
    resolvePathTileDrawGeometry,
    resolvePathTileTransformCenter,
    shouldClipPathTileToGrid,
} from '../pathTileRenderGeometry';
import { resolveTileDrawMetrics } from '../board';
import type { MapLayerRenderer } from '../MapLayerRenderer';
import type { Viewport } from '../viewport';

interface MapObjectPlacementPreviewState {
    active: boolean;
    assetId: string | null;
    previewMode: 'tile-anchor' | 'map-anchor';
    tileX?: number;
    tileY?: number;
    mapX?: number;
    mapY?: number;
    offsetX?: number;
    offsetY?: number;
    sizeMultiplier: number;
    flipHorizontal: boolean;
    flipVertical: boolean;
    rotationSteps: number;
}

interface PathTilePlacementPreviewState {
    active: boolean;
    atlasId: PathTileAtlasId | null;
    tileIndex: number | null;
    autotile: boolean;
    flipHorizontal: boolean;
    flipVertical: boolean;
    rotationSteps: number;
}

interface BiomeRegionPlacementPreviewState {
    active: boolean;
    biome: BiomeType | null;
    startX?: number;
    startY?: number;
    endX?: number;
    endY?: number;
}

export class PlacementPreviewRenderer {
    private mapObjectPlacementPreview: MapObjectPlacementPreviewState = {
        active: false,
        assetId: null,
        previewMode: 'tile-anchor',
        sizeMultiplier: 1,
        flipHorizontal: false,
        flipVertical: false,
        rotationSteps: 0,
    };

    private pathTilePlacementPreview: PathTilePlacementPreviewState = {
        active: false,
        atlasId: null,
        tileIndex: null,
        autotile: false,
        flipHorizontal: false,
        flipVertical: false,
        rotationSteps: 0,
    };

    private biomeRegionPlacementPreview: BiomeRegionPlacementPreviewState = {
        active: false,
        biome: null,
    };

    private readonly pathTileAtlasImages = new Map<string, HTMLImageElement>();
    private readonly biomePreviewImages = new Map<BiomeType, HTMLImageElement>();

    constructor(
        private readonly tileSize: number,
        private readonly requestRender: () => void,
    ) {}

    private handleMapObjectPlacementPreviewChanged = (event: Event): void => {
        const detail = (event as CustomEvent<MapObjectPlacementPreviewState>).detail;
        this.mapObjectPlacementPreview = {
            active: Boolean(detail?.active),
            assetId: detail?.assetId ?? null,
            previewMode: detail?.previewMode === 'map-anchor' ? 'map-anchor' : 'tile-anchor',
            tileX: typeof detail?.tileX === 'number' ? detail.tileX : undefined,
            tileY: typeof detail?.tileY === 'number' ? detail.tileY : undefined,
            mapX: typeof detail?.mapX === 'number' ? detail.mapX : undefined,
            mapY: typeof detail?.mapY === 'number' ? detail.mapY : undefined,
            offsetX: typeof detail?.offsetX === 'number' ? detail.offsetX : undefined,
            offsetY: typeof detail?.offsetY === 'number' ? detail.offsetY : undefined,
            sizeMultiplier: typeof detail?.sizeMultiplier === 'number' && detail.sizeMultiplier > 0 ? detail.sizeMultiplier : 1,
            flipHorizontal: Boolean(detail?.flipHorizontal),
            flipVertical: Boolean(detail?.flipVertical),
            rotationSteps: typeof detail?.rotationSteps === 'number' ? detail.rotationSteps : 0,
        };
    };

    private handlePathTilePlacementPreviewChanged = (event: Event): void => {
        const detail = (event as CustomEvent<PathTilePlacementPreviewState>).detail;
        this.pathTilePlacementPreview = {
            active: Boolean(detail?.active),
            atlasId: detail?.atlasId ?? null,
            tileIndex: typeof detail?.tileIndex === 'number' ? detail.tileIndex : null,
            autotile: detail?.autotile === true,
            flipHorizontal: Boolean(detail?.flipHorizontal),
            flipVertical: Boolean(detail?.flipVertical),
            rotationSteps: typeof detail?.rotationSteps === 'number' ? detail.rotationSteps : 0,
        };
    };

    private handleBiomeRegionPlacementPreviewChanged = (event: Event): void => {
        const detail = (event as CustomEvent<BiomeRegionPlacementPreviewState>).detail;
        this.biomeRegionPlacementPreview = {
            active: Boolean(detail?.active),
            biome: detail?.biome ?? null,
            startX: typeof detail?.startX === 'number' ? detail.startX : undefined,
            startY: typeof detail?.startY === 'number' ? detail.startY : undefined,
            endX: typeof detail?.endX === 'number' ? detail.endX : undefined,
            endY: typeof detail?.endY === 'number' ? detail.endY : undefined,
        };
        const biome = this.biomeRegionPlacementPreview.biome;
        if (!this.biomeRegionPlacementPreview.active || !biome || biome === 'default' || this.biomePreviewImages.has(biome)) {
            return;
        }
        const previewPath = getTerrainBiomeDefinition(biome)?.previewPath;
        if (!previewPath) {
            return;
        }
        const image = new Image();
        image.decoding = 'async';
        image.src = previewPath;
        image.onload = () => {
            this.biomePreviewImages.set(biome, image);
        };
    };

    public registerListeners(eventTarget: EventTarget): void {
        eventTarget.addEventListener('mapObjectPlacementPreviewChanged', this.handleMapObjectPlacementPreviewChanged as EventListener);
        eventTarget.addEventListener('pathTilePlacementPreviewChanged', this.handlePathTilePlacementPreviewChanged as EventListener);
        eventTarget.addEventListener('biomeRegionPlacementPreviewChanged', this.handleBiomeRegionPlacementPreviewChanged as EventListener);
    }

    public destroy(eventTarget: EventTarget): void {
        eventTarget.removeEventListener('mapObjectPlacementPreviewChanged', this.handleMapObjectPlacementPreviewChanged as EventListener);
        eventTarget.removeEventListener('pathTilePlacementPreviewChanged', this.handlePathTilePlacementPreviewChanged as EventListener);
        eventTarget.removeEventListener('biomeRegionPlacementPreviewChanged', this.handleBiomeRegionPlacementPreviewChanged as EventListener);
    }

    public drawMapObjectPlacementPreview(
        ctx: CanvasRenderingContext2D,
        interactionInfo: { tile: TilePosition | null; inFog: boolean },
        viewport: Viewport,
        mapLayer: MapLayerRenderer,
        viewportBounds: ScreenBounds,
    ): void {
        if (!this.mapObjectPlacementPreview.active || !this.mapObjectPlacementPreview.assetId) {
            return;
        }

        if (
            this.mapObjectPlacementPreview.previewMode === 'map-anchor'
            && Number.isFinite(this.mapObjectPlacementPreview.mapX)
            && Number.isFinite(this.mapObjectPlacementPreview.mapY)
        ) {
            const metrics = resolveMapObjectSpriteMetrics(
                {
                    id: '__preview__',
                    asset: this.mapObjectPlacementPreview.assetId,
                    sizeMultiplier: this.mapObjectPlacementPreview.sizeMultiplier,
                    flipHorizontal: this.mapObjectPlacementPreview.flipHorizontal,
                    flipVertical: this.mapObjectPlacementPreview.flipVertical,
                    rotationSteps: this.mapObjectPlacementPreview.rotationSteps,
                },
                this.tileSize,
            );
            if (!metrics) {
                return;
            }
            const anchor = viewport.worldCenterToScreen({
                x: this.mapObjectPlacementPreview.mapX! * this.tileSize,
                y: this.mapObjectPlacementPreview.mapY! * this.tileSize,
            });
            drawResolvedMapObjectSprite(ctx, metrics, anchor.x, anchor.y, 0.35);
            return;
        }

        const tileX = Number.isFinite(this.mapObjectPlacementPreview.tileX)
            ? this.mapObjectPlacementPreview.tileX
            : interactionInfo.tile?.x;
        const tileY = Number.isFinite(this.mapObjectPlacementPreview.tileY)
            ? this.mapObjectPlacementPreview.tileY
            : interactionInfo.tile?.y;

        if (!Number.isFinite(tileX) || !Number.isFinite(tileY) || (!interactionInfo.tile && !Number.isFinite(this.mapObjectPlacementPreview.tileX))) {
            return;
        }
        if (!Number.isFinite(this.mapObjectPlacementPreview.tileX) && !interactionInfo.tile) {
            return;
        }

        this.drawMapObjectSprite(
            ctx,
            {
                id: '__preview__',
                asset: this.mapObjectPlacementPreview.assetId,
                x: tileX,
                y: tileY,
                offsetX: this.mapObjectPlacementPreview.offsetX ?? 0,
                offsetY: this.mapObjectPlacementPreview.offsetY ?? 0,
                sizeMultiplier: this.mapObjectPlacementPreview.sizeMultiplier,
                flipHorizontal: this.mapObjectPlacementPreview.flipHorizontal,
                flipVertical: this.mapObjectPlacementPreview.flipVertical,
                rotationSteps: this.mapObjectPlacementPreview.rotationSteps,
            },
            viewport,
            mapLayer,
            0.35,
            viewportBounds,
        );
    }

    public drawPathTilePlacementPreview(
        ctx: CanvasRenderingContext2D,
        interactionInfo: { tile: TilePosition | null; inFog: boolean },
        viewport: Viewport,
        mapLayer: MapLayerRenderer,
    ): void {
        if (!this.pathTilePlacementPreview.active || !interactionInfo.tile) {
            return;
        }
        const atlasId = this.pathTilePlacementPreview.atlasId;
        if (!atlasId) {
            return;
        }
        const gridType = mapLayer.getMap().gridType;
        const atlas = getPathTileQuarterAtlasDefinition(atlasId, gridType);
        const image = this.pathTileAtlasImages.get(this.getPathAtlasCacheKey(atlasId, gridType));
        if (!image) {
            void this.ensurePathTileAtlasLoaded(atlasId, gridType);
        }
        if (!atlas) {
            return;
        }
        const previewRecommendation = this.pathTilePlacementPreview.autotile
            ? (() => {
                return recommendPathTileAt(
                    mapLayer.getMap(),
                    atlasId,
                    interactionInfo.tile.x,
                    interactionInfo.tile.y,
                );
            })()
            : null;
        const map = mapLayer.getMap();
        const manualVariantId = this.pathTilePlacementPreview.tileIndex === null
            ? null
            : encodeFixedPathVariantId(map.gridType, this.pathTilePlacementPreview.tileIndex);
        const previewCell = this.pathTilePlacementPreview.autotile
            ? previewRecommendation
                ? {
                    mode: 'autotile' as const,
                    surfaceTerrain: map.tiles[interactionInfo.tile.y]?.[interactionInfo.tile.x]?.terrain ?? 'floor',
                }
                : null
            : manualVariantId
                ? {
                    mode: 'fixed' as const,
                    variantId: manualVariantId,
                    surfaceTerrain: map.tiles[interactionInfo.tile.y]?.[interactionInfo.tile.x]?.terrain ?? 'floor',
                    flipHorizontal: this.pathTilePlacementPreview.flipHorizontal,
                    flipVertical: this.pathTilePlacementPreview.flipVertical,
                    rotationSteps: this.pathTilePlacementPreview.rotationSteps,
                }
                : null;
        if (!previewCell) {
            return;
        }
        const tileOffset = (interactionInfo.tile.y * map.width) + interactionInfo.tile.x;
        const previewMap = this.pathTilePlacementPreview.autotile
            ? {
                ...map,
                pathLayers: applyCanonicalPathTileEdit(map.width, map.height, map.pathLayers, {
                    atlasId,
                    tileOffset,
                    cell: previewCell,
                }, map.gridType),
            }
            : map;
        const resolvedPreview = pathAutotiler.resolveRenderTile(
            previewMap,
            atlasId,
            interactionInfo.tile.x,
            interactionInfo.tile.y,
            previewCell,
            atlas,
            image ?? null,
        );
        if (!resolvedPreview) {
            return;
        }
        const grid = mapLayer.getGrid();
        const tileMetrics = resolveTileDrawMetrics(grid, this.tileSize, interactionInfo.tile.x, interactionInfo.tile.y);
        const { drawWidth, drawHeight, boxCenterX, boxCenterY } = resolvePathTileDrawGeometry(
            map.gridType,
            this.tileSize,
            tileMetrics,
            atlas.drawScale ?? 1,
        );
        const transformCenter = resolvePathTileTransformCenter(map.gridType, tileMetrics);
        const screen = viewport.worldCenterToScreen({
            x: boxCenterX - drawWidth / 2,
            y: boxCenterY - drawHeight / 2,
        });

        ctx.save();
        ctx.globalAlpha *= 0.4;
        ctx.imageSmoothingEnabled = false;
        const previewRotationSteps = previewCell.mode === 'fixed' ? (previewCell.rotationSteps ?? 0) : 0;
        const previewFlipHorizontal = previewCell.mode === 'fixed' && previewCell.flipHorizontal === true;
        const previewFlipVertical = previewCell.mode === 'fixed' && previewCell.flipVertical === true;
        if (resolvedPreview.kind === 'image') {
            if (map.gridType === 'hex' || map.gridType === 'triangle') {
                drawGridClippedPathTileImage({
                    context: ctx,
                    grid,
                    gridType: map.gridType,
                    tileSize: this.tileSize,
                    tileX: interactionInfo.tile.x,
                    tileY: interactionInfo.tile.y,
                    boxCenterX: screen.x + drawWidth / 2,
                    boxCenterY: screen.y + drawHeight / 2,
                    transformCenterX: resolvedPreview.applyCellTransform && transformCenter
                        ? viewport.worldCenterToScreen(transformCenter).x
                        : undefined,
                    transformCenterY: resolvedPreview.applyCellTransform && transformCenter
                        ? viewport.worldCenterToScreen(transformCenter).y
                        : undefined,
                    drawWidth,
                    drawHeight,
                    image: resolvedPreview.image,
                    rotationSteps: getPathTileRenderRotationOffset(map.gridType)
                        + (resolvedPreview.applyCellTransform ? previewRotationSteps : 0),
                    flipHorizontal: resolvedPreview.applyCellTransform ? previewFlipHorizontal : false,
                    flipVertical: resolvedPreview.applyCellTransform ? previewFlipVertical : false,
                });
            } else if (resolvedPreview.applyCellTransform) {
                ctx.translate(screen.x + drawWidth / 2, screen.y + drawHeight / 2);
                if (previewRotationSteps !== 0) {
                    ctx.rotate((((previewRotationSteps % 12) + 12) % 12) * (Math.PI / 6));
                }
                ctx.scale(previewFlipHorizontal ? -1 : 1, previewFlipVertical ? -1 : 1);
                ctx.drawImage(resolvedPreview.image, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
            } else {
                ctx.drawImage(resolvedPreview.image, screen.x, screen.y, drawWidth, drawHeight);
            }
            ctx.restore();
            return;
        }
        if (!image) {
            ctx.restore();
            return;
        }
        const rotationSteps = getPathTileRenderRotationOffset(map.gridType) + resolvedPreview.rotationSteps;
        const sourceX = (resolvedPreview.tileIndex % atlas.columns) * atlas.tileSize;
        const sourceY = Math.floor(resolvedPreview.tileIndex / atlas.columns) * atlas.tileSize;
        if (shouldClipPathTileToGrid(map.gridType)) {
            ctx.restore();
            drawGridClippedPathTileImage({
                context: ctx,
                grid,
                gridType: map.gridType,
                tileSize: this.tileSize,
                tileX: interactionInfo.tile.x,
                tileY: interactionInfo.tile.y,
                boxCenterX: screen.x + drawWidth / 2,
                boxCenterY: screen.y + drawHeight / 2,
                drawWidth,
                drawHeight,
                image,
                sourceX,
                sourceY,
                sourceWidth: atlas.tileSize,
                sourceHeight: atlas.tileSize,
                rotationSteps,
                flipHorizontal: resolvedPreview.flipHorizontal,
                flipVertical: resolvedPreview.flipVertical,
            });
            return;
        }
        ctx.translate(screen.x + drawWidth / 2, screen.y + drawHeight / 2);
        if (rotationSteps !== 0) {
            ctx.rotate(((((rotationSteps % 12) + 12) % 12) * Math.PI) / 6);
        }
        ctx.scale(resolvedPreview.flipHorizontal ? -1 : 1, resolvedPreview.flipVertical ? -1 : 1);
        ctx.drawImage(
            image,
            sourceX,
            sourceY,
            atlas.tileSize,
            atlas.tileSize,
            -drawWidth / 2,
            -drawHeight / 2,
            drawWidth,
            drawHeight,
        );
        ctx.restore();
    }

    private getPathAtlasCacheKey(atlasId: PathTileAtlasId, gridType: string): string {
        return `${gridType}:${atlasId}`;
    }

    private async ensurePathTileAtlasLoaded(atlasId: PathTileAtlasId, gridType: string): Promise<void> {
        const cacheKey = this.getPathAtlasCacheKey(atlasId, gridType);
        if (this.pathTileAtlasImages.has(cacheKey)) {
            return;
        }
        const image = await loadPathTileAtlasImage(atlasId, gridType);
        this.pathTileAtlasImages.set(cacheKey, image);
        this.requestRender();
    }

    public drawBiomeRegionPlacementPreview(
        ctx: CanvasRenderingContext2D,
        mapLayer: MapLayerRenderer,
        viewport: Viewport,
    ): void {
        const preview = this.biomeRegionPlacementPreview;
        if (!preview.active || !preview.biome) {
            return;
        }
        const { startX, startY, endX, endY } = preview;
        if (
            !Number.isFinite(startX)
            || !Number.isFinite(startY)
            || !Number.isFinite(endX)
            || !Number.isFinite(endY)
        ) {
            return;
        }
        const minX = Math.min(startX!, endX!);
        const maxX = Math.max(startX!, endX!);
        const minY = Math.min(startY!, endY!);
        const maxY = Math.max(startY!, endY!);
        const grid = mapLayer.getGrid();
        const image = preview.biome === 'default' ? null : this.biomePreviewImages.get(preview.biome) ?? null;
        const fallbackColor = preview.biome === 'water'
            ? 'rgba(59, 130, 246, 0.28)'
            : preview.biome === 'sand'
                ? 'rgba(245, 158, 11, 0.24)'
                : preview.biome === 'grass' || preview.biome === 'soft_grass'
                    ? 'rgba(34, 197, 94, 0.24)'
                    : 'rgba(148, 163, 184, 0.22)';

        ctx.save();
        for (let y = minY; y <= maxY; y += 1) {
            for (let x = minX; x <= maxX; x += 1) {
                const screen = viewport.worldToScreen(x, y, grid);
                const cx = screen.x + this.tileSize / 2;
                const cy = screen.y + this.tileSize / 2;
                const tileMetrics = resolveTileDrawMetrics(grid, this.tileSize, x, y);
                const drawWidth = tileMetrics.spanWidth;
                const drawHeight = tileMetrics.spanHeight;

                if (image) {
                    ctx.save();
                    grid.drawTilePath(ctx, cx, cy, this.tileSize * 0.5, x, y);
                    ctx.clip();
                    ctx.globalAlpha = 0.42;
                    ctx.drawImage(image, screen.x, screen.y, drawWidth, drawHeight);
                    ctx.restore();
                } else {
                    ctx.fillStyle = fallbackColor;
                    grid.fillTile(ctx, cx, cy, this.tileSize * 0.48, x, y);
                }

                ctx.strokeStyle = 'rgba(255, 255, 255, 0.22)';
                ctx.lineWidth = 1;
                grid.drawTilePath(ctx, cx, cy, this.tileSize * 0.48, x, y);
                ctx.stroke();
            }
        }
        ctx.restore();
    }

    private drawMapObjectSprite(
        ctx: CanvasRenderingContext2D,
        object: MapObjectSpriteInput,
        viewport: Viewport,
        mapLayer: MapLayerRenderer,
        alpha: number,
        viewportBounds?: ScreenBounds,
    ): void {
        const metrics = resolveMapObjectSpriteMetrics(object, this.tileSize);
        if (!metrics) {
            return;
        }
        const grid = mapLayer.getGrid();
        const anchorWorld = resolveMapObjectWorldAnchor(object, this.tileSize, grid);
        const anchor = viewport.worldCenterToScreen(anchorWorld);
        const drawX = anchor.x - metrics.drawWidth / 2;
        const drawY = anchor.y + metrics.spriteCenterY - metrics.drawHeight / 2;
        if (viewportBounds && !isRectVisible(viewportBounds, drawX, drawY, metrics.drawWidth, metrics.drawHeight)) {
            return;
        }
        drawResolvedMapObjectSprite(ctx, metrics, anchor.x, anchor.y, alpha);
    }
}
